import React from 'react';

/**
 * TransitCard — the trip plan / departures card under a transit answer.
 *
 * Renders the /api/ask "itinerary" SSE event (app/services/transit/planner.py
 * JSON). Styled only from the widget theme `c` (the same settings as the chat:
 * accent, bubble and text colors, font, light/dark); the one exception is the
 * route badge, which uses the agency's own GTFS route color so "Route 4" looks
 * like the signs and buses riders know. Every link comes from the event (Google
 * Maps directions, the route's page), never from model text.
 */

const LABELS = {
  en: {
    trip: 'Trip plan', departures: 'Next departures', leave: 'Leave', arrive: 'Arrive', min: 'min',
    walk: 'Walk', to: 'to', route: 'Route', toward: 'toward', board: 'Board', getOff: 'Get off',
    stops: 'stops', transfer: 'transfer', transfers: 'transfers', longWalk: 'Long walk',
    later: 'Later options', earlier: 'Other options', alerts: 'Service alerts', maps: 'Open in Google Maps',
    routePage: (n) => `Route ${n} schedule`, live: 'Live', noTrip: 'No bus trip found',
    noService: 'No scheduled service that day', walkFaster: 'Walking may be faster',
    samePlace: 'These places are a short walk apart', nearestFrom: 'Nearest stop to the start',
    nearestTo: 'Nearest stop to the destination', noDepartures: 'No departures found',
    near: 'near', from: 'from', inMin: 'in', estimate: 'Walking distances are estimates.',
    schedule: 'From the published schedule', liveAge: 'live data', secondsAgo: 's ago',
    expected: 'expected', tomorrow: 'Tomorrow', routes: 'routes', lang: 'en',
  },
  es: {
    trip: 'Plan de viaje', departures: 'Próximas salidas', leave: 'Salga', arrive: 'Llegue', min: 'min',
    walk: 'Camine', to: 'a', route: 'Ruta', toward: 'hacia', board: 'Suba en', getOff: 'Bájese en',
    stops: 'paradas', transfer: 'transbordo', transfers: 'transbordos', longWalk: 'Caminata larga',
    later: 'Más opciones', earlier: 'Otras opciones', alerts: 'Avisos de servicio', maps: 'Abrir en Google Maps',
    routePage: (n) => `Horario de la ruta ${n}`, live: 'En vivo', noTrip: 'No se encontró un viaje en autobús',
    noService: 'No hay servicio programado ese día', walkFaster: 'Caminar puede ser más rápido',
    samePlace: 'Estos lugares están a poca distancia a pie', nearestFrom: 'Parada más cercana al inicio',
    nearestTo: 'Parada más cercana al destino', noDepartures: 'No se encontraron salidas',
    near: 'cerca de', from: 'desde', inMin: 'en', estimate: 'Las distancias a pie son aproximadas.',
    schedule: 'Según el horario publicado', liveAge: 'datos en vivo', secondsAgo: 's',
    expected: 'estimado', tomorrow: 'Mañana', routes: 'rutas', lang: 'es',
  },
};

const HEX = /^[0-9A-Fa-f]{6}$/;

/** Black or white, whichever reads better on the given background. */
export function readableOn(hex) {
  if (!HEX.test(hex || '')) return '#ffffff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? '#111111' : '#ffffff';
}

/** The route badge colors: the agency's own, else the widget accent. */
export function badgeColors(route, c) {
  const bg = HEX.test(route?.color || '') ? `#${route.color}` : c.accent;
  const fg = HEX.test(route?.text_color || '') && route.color ? `#${route.text_color}`
    : (HEX.test(route?.color || '') ? readableOn(route.color) : (c.accentText || '#ffffff'));
  return { bg, fg };
}

/** Only http(s) links from the event become anchors. */
function safeUrl(url) {
  return /^https?:\/\//i.test(url || '') ? url : null;
}

function RouteBadge({ route, c, t }) {
  const { bg, fg } = badgeColors(route, c);
  return (
    <span aria-label={`${t.route} ${route.short}`} style={{
      display: 'inline-block', minWidth: 26, padding: '1px 6px', borderRadius: 5, background: bg, color: fg,
      fontSize: 12.5, fontWeight: 700, textAlign: 'center', lineHeight: 1.5, flexShrink: 0,
    }}>{route.short || '?'}</span>
  );
}

// The server's live status is English ("on time", "3 min late", "2 min
// early", "canceled"); the card shows it in the answer's language.
const STATUS_ES = [
  [/^on time$/, () => 'a tiempo'], [/^(\d+) min late$/, (m) => `${m[1]} min de retraso`],
  [/^(\d+) min early$/, (m) => `${m[1]} min antes`], [/^canceled$/, () => 'cancelado'],
];

export function liveStatus(status, lang) {
  if (lang !== 'es') return status;
  for (const [re, fmt] of STATUS_ES) {
    const m = re.exec(status || '');
    if (m) return fmt(m);
  }
  return status;
}

function Live({ live, c, t }) {
  if (!live) return null;
  const late = /late|canceled/i.test(live.status);
  return (
    <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 600, color: late ? c.error : c.muted }}>
      {t.live}: {liveStatus(live.status, t.lang)}
    </span>
  );
}

function WalkIcon({ color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }}>
      <circle cx="13" cy="4" r="2" fill={color} />
      <path d="M10 22l2-7 2 3v4h2v-5.5l-2-3 .6-3A6 6 0 0019 12v-2a4 4 0 01-3.4-2l-1-1.6A2 2 0 0012.9 5l-4.4 1.8V11h2V8.2l1.4-.5L10 16l-4 .7.3 2 5-.9z"
            fill={color} />
    </svg>
  );
}

function Leg({ leg, c, t }) {
  if (leg.mode === 'walk') {
    return (
      <li style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '4px 0' }}>
        <WalkIcon color={c.muted} />
        <span style={{ color: c.text }}>
          {t.walk} {leg.distance_mi} mi ({leg.minutes} {t.min}) {t.to} <strong>{leg.to}</strong>
        </span>
      </li>
    );
  }
  const r = leg.route;
  return (
    <li style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '4px 0' }}>
      <RouteBadge route={r} c={c} t={t} />
      <span style={{ color: c.text, minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>{t.route} {r.short}{r.long ? ` · ${r.long}` : ''}</span>
        <span style={{ color: c.muted }}> {t.toward} {leg.headsign}</span>
        <br />
        {t.board} <strong>{leg.board.stop}</strong> · {leg.board.time}
        {leg.board.expected && <span style={{ color: c.muted }}> ({t.expected} {leg.board.expected})</span>}
        <Live live={leg.live} c={c} t={t} />
        <br />
        {t.getOff} <strong>{leg.alight.stop}</strong> · {leg.alight.time}
        {leg.stops ? <span style={{ color: c.muted }}> · {leg.stops} {t.stops}</span> : null}
      </span>
    </li>
  );
}

function Alerts({ alerts, c, t }) {
  if (!alerts || !alerts.length) return null;
  return (
    <div role="note" style={{ marginTop: 8, padding: '6px 8px', borderRadius: 6, border: `1px solid ${c.border}`,
                              fontSize: 12.5, color: c.text }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>⚠ {t.alerts}</div>
      {alerts.slice(0, 3).map((a, i) => (
        <div key={i} style={{ marginTop: i ? 4 : 0 }}>
          {a.routes && a.routes.length > 0 && <span style={{ color: c.muted }}>{a.routes.join(', ')}: </span>}
          {safeUrl(a.url)
            ? <a href={safeUrl(a.url)} target="_blank" rel="noopener noreferrer" className="inquiros-inline-link">{a.header}</a>
            : a.header}
        </div>
      ))}
    </div>
  );
}

function Footer({ links, note, c }) {
  return (
    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'baseline' }}>
      {links.filter((l) => safeUrl(l.url)).map((l) => (
        <a key={l.url} href={safeUrl(l.url)} target="_blank" rel="noopener noreferrer"
           className="inquiros-inline-link" style={{ fontSize: 12.5 }}>{l.label}</a>
      ))}
      {note && <span style={{ fontSize: 11.5, color: c.muted }}>{note}</span>}
    </div>
  );
}

function Nearest({ nearest, c, t }) {
  if (!nearest) return null;
  const rows = [[t.nearestFrom, nearest.from], [t.nearestTo, nearest.to]].filter(([, n]) => n);
  return rows.map(([label, n]) => (
    <div key={label} style={{ fontSize: 13, color: c.text, marginTop: 4 }}>
      {label}: <strong>{n.stop}</strong> · {n.distance_mi} mi
      {n.routes && n.routes.length ? <span style={{ color: c.muted }}> ({t.routes} {n.routes.join(', ')})</span> : null}
    </div>
  ));
}

function sourceNote(it, t) {
  const live = typeof it.live_age_s === 'number' ? ` · ${t.liveAge} ${it.live_age_s}${t.secondsAgo}` : '';
  return `${t.schedule}${it.agency ? ` (${it.agency})` : ''}${live}.`;
}

function TripCard({ it, c, t }) {
  const [first, ...rest] = it.itineraries || [];
  const routeLinks = [];
  (first ? first.legs : []).forEach((l) => {
    if (l.mode === 'transit' && safeUrl(l.route.url) && !routeLinks.some((x) => x.url === l.route.url)) {
      routeLinks.push({ url: l.route.url, label: t.routePage(l.route.short) });
    }
  });
  const links = [{ url: it.maps_url, label: t.maps }, ...routeLinks];
  const header = (
    <div style={{ fontSize: 13, color: c.muted }}>
      <span style={{ fontWeight: 700, color: c.text }}>{t.trip}</span>
      {' · '}{it.from.label} → {it.to.label}
    </div>
  );
  if (!first) {
    const title = it.status === 'no_service' ? t.noService : it.status === 'same_place' ? t.samePlace
      : it.status === 'walk' ? t.walkFaster : t.noTrip;
    return (
      <>
        {header}
        <div style={{ marginTop: 6, fontWeight: 600, color: c.text }}>{title}</div>
        {it.walk_only && (
          <div style={{ fontSize: 13, color: c.text, marginTop: 4 }}>
            {t.walk} {it.walk_only.distance_mi} mi ({it.walk_only.minutes} {t.min})
          </div>
        )}
        <Nearest nearest={it.nearest} c={c} t={t} />
        <Footer links={links} note={t.estimate} c={c} />
      </>
    );
  }
  const transfers = first.transfers === 1 ? `1 ${t.transfer}` : `${first.transfers} ${t.transfers}`;
  return (
    <>
      {header}
      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '2px 10px', alignItems: 'baseline' }}>
        {first.day !== 'today' && (
          <span style={{ fontSize: 12, fontWeight: 700, color: c.accent }}>
            {first.day === 'tomorrow' ? t.tomorrow : first.day}
          </span>
        )}
        <span style={{ fontSize: 15, fontWeight: 700, color: c.text }}>
          {t.leave} {first.depart} → {t.arrive} {first.arrive}
        </span>
        <span style={{ fontSize: 12.5, color: c.muted }}>{first.duration_min} {t.min} · {transfers}</span>
      </div>
      {first.long_walk && (
        <div style={{ marginTop: 4, fontSize: 12.5, fontWeight: 600, color: c.error }}>{t.longWalk}</div>
      )}
      <ol style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, fontSize: 13.5, lineHeight: 1.45 }}>
        {first.legs.map((leg, i) => <Leg key={i} leg={leg} c={c} t={t} />)}
      </ol>
      {rest.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: c.muted }}>
          {it.requested && it.requested.mode === 'arrive' ? t.earlier : t.later}:{' '}
          {rest.map((o, i) => {
            const routes = o.legs.filter((l) => l.mode === 'transit').map((l) => l.route.short).join(' → ');
            return (
              <span key={i} style={{ color: c.text }}>
                {i > 0 ? ' · ' : ''}{o.depart} → {o.arrive} ({routes})
              </span>
            );
          })}
        </div>
      )}
      <Alerts alerts={it.alerts} c={c} t={t} />
      <Footer links={links} note={`${sourceNote(it, t)} ${t.estimate}`} c={c} />
    </>
  );
}

function DeparturesCard({ it, c, t }) {
  const links = [{
    url: `https://www.google.com/maps/search/?api=1&query=${it.place.lat},${it.place.lon}`, label: t.maps,
  }];
  return (
    <>
      <div style={{ fontSize: 13, color: c.muted }}>
        <span style={{ fontWeight: 700, color: c.text }}>{t.departures}</span>
        {' · '}{t.near} {it.place.label}
      </div>
      {(!it.departures || !it.departures.length) ? (
        <>
          <div style={{ marginTop: 6, fontWeight: 600, color: c.text }}>{t.noDepartures}</div>
          {it.nearest && <Nearest nearest={{ from: it.nearest }} c={c} t={t} />}
        </>
      ) : (
        <ol style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, fontSize: 13.5, lineHeight: 1.45 }}>
          {it.departures.map((d, i) => (
            <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '4px 0' }}>
              <RouteBadge route={d.route} c={c} t={t} />
              <span style={{ flex: 1, minWidth: 0, color: c.text }}>
                <span style={{ fontWeight: 600 }}>{d.headsign}</span>
                <span style={{ color: c.muted }}> · {t.from} {d.stop}</span>
                <Live live={d.live} c={c} t={t} />
              </span>
              <span style={{ textAlign: 'right', whiteSpace: 'nowrap', color: c.text }}>
                <strong>{d.expected || d.time}</strong>
                <br />
                <span style={{ fontSize: 12, color: c.muted }}>
                  {d.day === 'today' ? `${t.inMin} ${d.minutes_away} ${t.min}` : (d.day === 'tomorrow' ? t.tomorrow : d.day)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
      <Alerts alerts={it.alerts} c={c} t={t} />
      <Footer links={links} note={sourceNote(it, t)} c={c} />
    </>
  );
}

export function TransitCard({ itinerary, c, lang = 'en' }) {
  if (!itinerary || (itinerary.kind !== 'trip' && itinerary.kind !== 'departures')) return null;
  const t = LABELS[lang] || LABELS.en;
  const isTrip = itinerary.kind === 'trip';
  return (
    <section
      aria-label={isTrip ? t.trip : t.departures}
      className="inquiros-transit-card"
      style={{
        marginTop: 10, padding: '10px 12px', borderRadius: 10, border: `1px solid ${c.border}`,
        background: c.bg, whiteSpace: 'normal', fontFamily: 'inherit',
      }}
    >
      {isTrip ? <TripCard it={itinerary} c={c} t={t} /> : <DeparturesCard it={itinerary} c={c} t={t} />}
    </section>
  );
}

export default TransitCard;
